import type {
  TaskFormApiField,
  TaskFormApiFieldValidation,
  TaskFormApiSection,
  TaskFormDetailResponse,
  FormBuilderState,
  FormField,
  FormSection,
  FieldType,
} from '../types';

const VALID_FIELD_TYPES = new Set<FieldType>([
  'text',
  'textarea',
  'number',
  'number_range',
  'date',
  'date_range',
  'dropdown',
  'radio',
  'checkbox',
  'attachment',
  'price',
]);

function mapApiField(apiField: TaskFormApiField): FormField {
  const { id, type, config, validationRules } = apiField;
  const rules: Partial<TaskFormApiFieldValidation> = validationRules ?? {};

  const isRangeNumber =
    type === 'number' && (rules.min != null || rules.max != null);
  const isRangeDate = type === 'date' && config.variant === 'range';

  const resolvedType: FieldType | '' = isRangeNumber
    ? 'number_range'
    : isRangeDate
      ? 'date_range'
      : VALID_FIELD_TYPES.has(type as FieldType)
        ? (type as FieldType)
        : '';

  const field: FormField = {
    id,
    type: resolvedType,
    label: config.label,
    required: rules.required ?? false,
  };

  if (config.uom) {
    field.unit = config.uom;
  }
  if (rules.min != null) {
    field.min = rules.min;
  }
  if (rules.max != null) {
    field.max = rules.max;
  }
  if (config.multiSelect) {
    field.multiSelect = config.multiSelect;
  }

  if (config.options && config.options.length > 0) {
    field.options = config.options.map((opt) => ({
      id: opt.value,
      label: opt.label,
      value: opt.value,
    }));
  }

  if (config.acceptedFileTypes && config.acceptedFileTypes.length > 0) {
    field.acceptedFileTypes = config.acceptedFileTypes.map((t) =>
      t.startsWith('.') ? t.slice(1).toUpperCase() : t.toUpperCase()
    );
  }

  if (config.maxFileSizeMb != null) {
    field.maxFileSizeMb = config.maxFileSizeMb;
  }
  if (config.maxFiles != null) {
    field.maxFiles = config.maxFiles;
  }

  return field;
}

function mapApiSection(apiSection: TaskFormApiSection): FormSection {
  const sortedFields = [...apiSection.fields]
    .filter((f) => f.type !== 'signature')
    .sort((a, b) => (a.ui?.order ?? 0) - (b.ui?.order ?? 0));
  return {
    id: apiSection.id,
    name: apiSection.name,
    description: apiSection.description ?? '',
    fields: sortedFields.map(mapApiField),
  };
}

export function mapApiFormToBuilderState(
  form: TaskFormDetailResponse
): FormBuilderState {
  const sortedSections = [...form.schema.sections].sort(
    (a, b) => a.order - b.order
  );
  return {
    name: form.name,
    description: form.description ?? '',
    sections: sortedSections.map(mapApiSection),
    signatureRequired: form.schema.signatureRequired ?? true,
  };
}
